import type { FilterStateSelectedItemsType } from '@xing-com/crate-entity-pages-jobs';

import type {
  JobsDetailQuery,
  JobsDetailQueryVariables,
} from '../graphql/queries/jobs-detail-query.gql-types';

export const LIST_LIMIT = 10;

type JobsDetailResult = NonNullable<JobsDetailQuery['jobSearchByQuery']>;
type JobsDetailAggregations = JobsDetailResult['aggregations'];

export type JobsFilterAggregations = {
  careerLevels: Array<{
    id: string;
    careerLevel: {
      localizationKey: string;
      localizationValue: string;
    };
  }>;
};

/** The error flag of React Apollo is not reliable when using SSR, therefore we check if there are errors like this */
export const isError = (data: JobsDetailQuery | undefined) => {
  return !data?.jobSearchByQuery?.collection;
};

export const buildJobsDetailVariables = ({
  companyId,
  isLoggedIn,
  keywords = '',
  selectedItems = { careerLevels: [] },
  offset,
}: {
  companyId: string;
  isLoggedIn: boolean;
  keywords?: string;
  selectedItems?: FilterStateSelectedItemsType;
  offset?: number;
}): JobsDetailQueryVariables => {
  const variables: JobsDetailQueryVariables = {
    isLoggedIn,
    limit: LIST_LIMIT,
    query: {
      filter: {
        company: { id: [companyId] },
        publishToCompany: null,
      },
    },
  };

  if (typeof offset === 'number') {
    variables.offset = offset;
  }

  if (keywords) {
    variables.query.keywords = `${keywords}*`;
  }

  if (selectedItems.careerLevels?.length && variables.query.filter) {
    variables.query.filter.careerLevel = {
      id: selectedItems.careerLevels,
    };
  }

  return variables;
};

export const buildJobsFilterAggregations = (
  aggregations?: JobsDetailAggregations | null
): JobsFilterAggregations | undefined => {
  if (!aggregations) {
    return undefined;
  }

  return {
    careerLevels: (aggregations.careerLevels ?? []).flatMap((careerLevel) => {
      if (!careerLevel?.careerLevel) {
        return [];
      }

      return [
        {
          id: careerLevel.id,
          careerLevel: careerLevel.careerLevel,
        },
      ];
    }),
  };
};

export const mergeJobsDetailResults = (
  previousResult: JobsDetailQuery,
  fetchMoreResult: JobsDetailQuery
): JobsDetailQuery => {
  const previousJobSearch = previousResult.jobSearchByQuery;
  const nextJobSearch = fetchMoreResult.jobSearchByQuery;

  if (!previousJobSearch || !nextJobSearch) {
    return previousResult;
  }

  return {
    ...fetchMoreResult,
    jobSearchByQuery: {
      ...nextJobSearch,
      collection: [
        ...previousJobSearch.collection,
        ...nextJobSearch.collection,
      ],
    },
  };
};
